import { useMemo, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ObjectProposalGenerationType } from '@econtractor/types';
import { useObjectCard } from '../../context/ObjectCardProvider';
import { useToast } from 'context/ToastContext';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import * as apiDocument from 'api/apiDocument';
import * as apiProposal from 'api/apiProposal';
import {
    ObjectDocumentEditor,
    resolveObjectClientEmail,
    resolveObjectId,
    useObjectDocumentGenerations,
    useSendGenerationEmailDialog,
    type DocumentItemsOverride,
} from 'modules/templates';
import { buildDocumentMailContent } from 'utils/helpers/email/buildMailContent';
import SvgTrash from 'components/svg/SvgTrash';
import ESpinner from 'components/others/Spinner';
import SpinnerBlock from 'components/others/SpinnerBlock';
import EToolTip from 'components/others/EToolTip';
import OffUploadDocument from '../../OffUploadDocument';
import OffDocumentPayments, {
    type DocumentPaymentTarget,
} from '../../OffDocumentPayments';
import Proposal2Warehouse, {
    type Proposal2WarehouseTarget,
} from '../../Proposal2Warehouse';
import DocumentsTableBody from './DocumentsTableBody';
import SolarIcon from 'components/icons/SolarIcon';
import vconst from 'utils/helpers/structs/vconst';

const buildItemsOverrideFromGeneration = (row: ObjectProposalGenerationType): DocumentItemsOverride => ({
    products: Array.isArray(row.products) ? row.products : [],
    additionalItems: Array.isArray(row.additionalItems) ? row.additionalItems : [],
    pricesInUsd: Boolean(row.proposalUsd),
    proposalVat: Boolean(row.proposalVat),
});

const ObjectTabDocuments = () => {
    const { t } = useTranslation(['Objects', 'Document', 'Proposal', 'common']);
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const { vObject, fetchObject, isEditLocked } = useObjectCard();
    const { openSendGenerationEmail } = useSendGenerationEmailDialog();
    const [selectOpen, setSelectOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [paymentTarget, setPaymentTarget] = useState<DocumentPaymentTarget | null>(null);
    const [warehouseTarget, setWarehouseTarget] = useState<Proposal2WarehouseTarget | null>(null);
    const [itemsOverride, setItemsOverride] = useState<DocumentItemsOverride | null>(null);
    const objectId = useMemo(() => resolveObjectId(vObject), [vObject]);
    const clientEmail = useMemo(() => resolveObjectClientEmail(vObject), [vObject]);
    const hasResolvedObject = !objectId || Boolean(vObject?.object);
    const objectStatus = vObject?.object?.objectStatus;
    const hideDocumentsToolbar =
        Number(objectStatus) === vconst.objectFinished || Number(objectStatus) === vconst.objectReject;
    const showDocumentsToolbar = hasResolvedObject && !hideDocumentsToolbar;
    const { rows, loading, refreshing, deletingId, refresh, markDeleted } =
        useObjectDocumentGenerations(objectId);

    const openCreateDocument = (override: DocumentItemsOverride | null = null) => {
        setItemsOverride(override);
        setSelectOpen(true);
    };

    const handleCreateFromGeneration = (row: ObjectProposalGenerationType) => {
        openCreateDocument(buildItemsOverrideFromGeneration(row));
    };

    const handlePayment = (row: ObjectProposalGenerationType) => {
        if ((row.source || 'object') !== 'object' || Number(row.deleted || 0)) return;
        setPaymentTarget({
            generationId: row.ID,
            title: row.proposalName || t('Document:card.noName'),
        });
    };

    const handleToWarehouse = (row: ObjectProposalGenerationType) => {
        if ((row.source || 'object') !== 'lead' || Number(row.deleted || 0)) return;
        setWarehouseTarget({
            generationId: row.ID,
            title: row.proposalName || t('Document:card.noName'),
            products: Array.isArray(row.products) ? row.products : [],
            additionalItems: Array.isArray(row.additionalItems) ? row.additionalItems : [],
            pricesInUsd: Boolean(row.proposalUsd),
        });
    };

    const handleDelete = (row: ObjectProposalGenerationType) => {
        if ((row.source || 'object') !== 'object' || Number(row.deleted || 0)) return;
        const name = row.proposalName || t('Document:card.noName');
        const buttons = getButtonsNoYesDanger(t).map((button) => ({
            ...button,
            handleClick: undefined as (() => void) | undefined,
        }));
        buttons[1].handleClick = () => {
            showDlg(false);
            void (async () => {
                const stat = await markDeleted(row.ID);
                if (!stat) {
                    showToast(t('common:messError'), 'danger');
                    return;
                }
                showToast(t('documents.messDeleted'), 'success');
                if (fetchObject) await fetchObject();
            })();
        };
        showDlg({
            buttons,
            type: 'confirm',
            text: (
                <>
                    <h4 className="d-flex pb-2 mb-2 border-bottom text-danger">
                        <SvgTrash className="text-danger" />
                        <span className="ps-2">{t('documents.confirmDelete')}</span>
                    </h4>
                    <h5 className="mb-0">{name}</h5>
                    {row.generationComment ? (
                        <small className="form-text text-muted">{row.generationComment}</small>
                    ) : null}
                </>
            ),
        });
    };

    const handleSendEmail = (row: ObjectProposalGenerationType) => {
        if (Number(row.deleted || 0)) return;
        const isLeadProposal = (row.source || 'object') === 'lead';
        const fileUrl = String(row.filePublicUrl || '').trim();
        openSendGenerationEmail({
            defaultEmail: clientEmail || '',
            onSubmit: async ({ email, emailBody }) => {
                if (!fileUrl) {
                    showToast(t('common:messError'), 'danger');
                    return false;
                }
                const mailContent = await buildDocumentMailContent({
                    type: isLeadProposal ? 'proposal' : row.proposalType || 'other',
                    documentName: row.proposalName || row.fileName || 'document',
                    fileUrl,
                    emailBody,
                });
                if (!mailContent) {
                    showToast(t('common:messError'), 'danger');
                    return false;
                }

                let stat = false;
                if (isLeadProposal) {
                    const leadId = Number(row.leadId || 0);
                    if (!leadId) {
                        showToast(t('common:messError'), 'danger');
                        return false;
                    }
                    stat = await apiProposal.sendLeadProposalGenerationEmail(leadId, row.ID, {
                        email,
                        emailBody,
                        mailContent,
                    });
                } else {
                    if (!objectId) {
                        showToast(t('common:messError'), 'danger');
                        return false;
                    }
                    stat = await apiDocument.sendObjectDocumentGenerationEmail(objectId, row.ID, {
                        email,
                        emailBody,
                        mailContent,
                    });
                }
                if (!stat) {
                    showToast(t('common:messError'), 'danger');
                    return false;
                }
                showToast(t('Proposal:edit.generate.sendDialog.success'), 'success');
                await refresh();
                return true;
            },
        });
    };

    return (
        <>
            <ObjectDocumentEditor
                objectData={vObject}
                selectOpen={selectOpen}
                onSelectClose={() => setSelectOpen(false)}
                onGenerated={refresh}
                itemsOverride={itemsOverride}
                onItemsOverrideClear={() => setItemsOverride(null)}
            />

            <OffUploadDocument
                show={uploadOpen}
                setShow={setUploadOpen}
                objectId={objectId}
                onUploaded={refresh}
            />

            <OffDocumentPayments
                objectId={objectId}
                showTarget={paymentTarget}
                setShowTarget={setPaymentTarget}
                onPaid={refresh}
            />

            <Proposal2Warehouse
                showTarget={warehouseTarget}
                setShowTarget={setWarehouseTarget}
            />

            {showDocumentsToolbar ? (
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-3">
                    <div className="d-flex gap-2">
                        {!isEditLocked ? (
                            <>
                                <Button size="sm" variant="primary" onClick={() => openCreateDocument(null)}>
                                    {t('documents.btnAdd')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => setUploadOpen(true)}
                                    disabled={!objectId}
                                >
                                    {t('documents.btnUpload')}
                                </Button>
                            </>
                        ) : null}
                    </div>
                    <EToolTip title={t('Document:edit.generate.logRefresh')}>
                        <span className="d-inline-block ms-auto">
                            <Button
                                size="sm"
                                variant="primary"
                                className="fs-14 px-2"
                                onClick={() => void refresh()}
                                disabled={loading || refreshing}
                                aria-label={t('Document:edit.generate.logRefresh')}
                            >
                                <SolarIcon name="arrow-clockwise" className="fs-14 p-0" />
                            </Button>
                        </span>
                    </EToolTip>
                </div>
            ) : null}

            <div className={`pb-4 position-relative ${showDocumentsToolbar ? 'pt-2' : 'pt-0'}`}>
                <SpinnerBlock onlyBlur={true} show={refreshing} />
                {loading ? (
                    <ESpinner />
                ) : rows.length ? (
                    <div className="proposal-generate-log-table-wrap">
                        <Table
                            size="sm"
                            className={`ProposalGenerateLogTable table-hover mb-0 align-middle ${
                                showDocumentsToolbar ? 'mt-2' : 'mt-0'
                            }`}
                        >
                            <thead>
                                <tr className="bg-light">
                                    <td className="head-user">{t('Document:edit.generate.logUser')}</td>
                                    <td className="head-date">{t('Document:edit.generate.logDate')}</td>
                                    <td className="head-type">{t('documents.logType')}</td>
                                    <td className="head-email">{t('Document:edit.generate.logEmail')}</td>
                                    <td className="head-amount text-end">{t('Document:edit.generate.logAmount')}</td>
                                    <td className="head-comment">{t('Document:edit.generate.logComment')}</td>
                                    <td className="head-file">{t('Document:edit.generate.logFile')}</td>
                                    <td className="head-actions">{t('documents.logActions')}</td>
                                </tr>
                            </thead>
                            <DocumentsTableBody
                                rows={rows}
                                deletingId={deletingId}
                                onCreateFromGeneration={handleCreateFromGeneration}
                                onToWarehouse={handleToWarehouse}
                                onPayment={handlePayment}
                                onDelete={handleDelete}
                                onSendEmail={handleSendEmail}
                            />
                        </Table>
                    </div>
                ) : (
                    <div className="text-muted fs-12 py-4">{t('documents.logEmpty')}</div>
                )}
            </div>
        </>
    );
};

export default ObjectTabDocuments;
